import { useId, useRef, useState } from 'react';
import type { PickerOption, PickerResults } from '../../shared/exercisePicker';
import { searchExercises } from '../../shared/exercisePicker';
import { ExerciseMedia, useExerciseMedia } from '../media/ExerciseMedia';
import type { ExerciseMediaSet } from '../media/ExerciseMedia';

/**
 * The one field the coach fills in first, and the only one that got harder when
 * the catalogue arrived.
 *
 * It is still a text field. She can type a movement nobody has ever heard of
 * and add it, exactly as before — the catalogue is an offer, never a
 * requirement, and a picker that refused an unknown name would make her
 * vocabulary the product's problem rather than hers.
 *
 * What is new is what happens while she types: one search across name, muscle
 * and equipment, her own exercises pinned above the stock ones in a single
 * list, and her recent exercises offered before she has typed anything at all.
 * The ranking lives in `shared/exercisePicker.ts`, where it can be tested
 * against the real catalogue.
 *
 * The list closes on blur. That is not decoration: the form beneath it is a
 * flex row that wraps, so a list that stayed open could cover the button she
 * is reaching for.
 *
 *   exercise-catalogue, "The product ships with a searchable stock exercise
 *   catalogue"
 *     #### Scenario: Adding a standard exercise costs a search
 *     #### Scenario: Her own exercises come first
 *     #### Scenario: An empty field is not an empty screen
 */
export function ExercisePicker({
  value,
  onChange,
  onPick,
  onEdit,
  options,
  recents,
  error,
  className,
}: {
  value: string;
  onChange: (value: string) => void;
  /** A row she chose, or `null` when she went back to typing her own words. */
  onPick: (option: PickerOption | null) => void;
  /** She wants to put her own name and cue on one of her own rows. */
  onEdit: (option: PickerOption) => void;
  options: readonly PickerOption[];
  recents: readonly PickerOption[];
  error?: string | undefined;
  className?: string;
}) {
  const id = useId();
  const listId = `${id}-list`;
  const errorId = `${id}-error`;
  const [open, setOpen] = useState(false);
  const closing = useRef<number | undefined>(undefined);

  const results: PickerResults = searchExercises(value, options, recents);
  const rows = [...results.mine, ...results.stock];
  const empty = rows.length === 0;

  // One signing call for the rows on screen, and none at all while the media is
  // switched off — which is the state the product ships in.
  const media = useExerciseMedia(rows.map((option) => option.mediaKey));

  const choose = (option: PickerOption) => {
    onChange(option.name);
    onPick(option);
    setOpen(false);
  };

  return (
    <div className={['relative flex flex-col gap-1.5', className ?? ''].join(' ')}>
      <label htmlFor={id} className="text-ink text-sm font-medium">
        Exercise
      </label>

      <input
        id={id}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        autoComplete="off"
        placeholder="Goblet squat"
        value={value}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        className={[
          'bg-surface-raised text-ink min-h-11 rounded-[--radius-control] border px-3 text-base',
          error ? 'border-danger' : 'border-border-strong',
        ].join(' ')}
        onChange={(event) => {
          onChange(event.target.value);
          // Typing over a chosen row un-chooses it. Anything else would link
          // the entry to an exercise whose name is no longer on the screen.
          onPick(null);
          setOpen(true);
        }}
        onFocus={() => {
          window.clearTimeout(closing.current);
          setOpen(true);
        }}
        onBlur={() => {
          // Deferred by a tick so that a click on a row is registered before
          // the list it was in stops existing.
          closing.current = window.setTimeout(() => {
            setOpen(false);
          }, 0);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') setOpen(false);
        }}
      />

      {open ? (
        <div
          id={listId}
          role="listbox"
          // Named "Suggestions" rather than "Exercises" on purpose: the field
          // beside it is already labelled "Exercise", and two controls whose
          // accessible names differ by one letter are ambiguous to a screen
          // reader and to anything else looking one of them up by name.
          aria-label="Suggestions"
          className="border-border-strong bg-surface-raised absolute top-full left-0 z-20 mt-1 max-h-80 w-full min-w-72 overflow-y-auto rounded-[--radius-control] border shadow-lg"
        >
          {empty ? (
            <p className="text-ink-muted px-3 py-2 text-sm">
              {value.trim() === ''
                ? 'Start typing, or add an exercise in your own words.'
                : 'No match — add it in your own words.'}
            </p>
          ) : null}

          {results.mine.length > 0 ? (
            <Section label={value.trim() === '' ? 'Recently used' : 'Your exercises'} />
          ) : null}
          {results.mine.map((option) => (
            <Row
              key={option.key}
              option={option}
              media={media}
              onChoose={choose}
              onEdit={
                option.libraryItemId === null
                  ? undefined
                  : (row) => {
                      choose(row);
                      onEdit(row);
                    }
              }
            />
          ))}

          {results.stock.length > 0 ? <Section label="All exercises" /> : null}
          {results.stock.map((option) => (
            <Row key={option.key} option={option} media={media} onChoose={choose} />
          ))}

          {results.omitted > 0 ? (
            <p className="text-ink-muted border-border border-t px-3 py-2 text-xs">
              {results.omitted} more match. Add a word — a muscle or a piece of kit — to narrow it.
            </p>
          ) : null}
        </div>
      ) : null}

      {error ? (
        <p id={errorId} className="text-danger text-xs">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function Section({ label }: { label: string }) {
  return (
    <p className="text-ink-muted bg-surface-muted px-3 py-1 text-xs font-medium tracking-wide">
      {label}
    </p>
  );
}

function Row({
  option,
  media,
  onChoose,
  onEdit,
}: {
  option: PickerOption;
  media: ExerciseMediaSet;
  onChoose: (option: PickerOption) => void;
  onEdit?: ((option: PickerOption) => void) | undefined;
}) {
  const detail = [
    option.equipment,
    option.target,
    option.stockName === null ? null : `from ${option.stockName}`,
  ]
    .filter((part) => part !== null)
    .join(' · ');

  // A wrapper rather than one big button, because the row has two actions and a
  // button inside a button is not markup a browser will keep.
  return (
    <div role="presentation" className="hover:bg-primary-tint flex w-full items-center">
      <button
        type="button"
        role="option"
        aria-selected={false}
        // `onMouseDown` rather than `onClick`: the input's blur fires first, and
        // a click handler on a list that blur has already closed never runs.
        onMouseDown={(event) => {
          event.preventDefault();
          onChoose(option);
        }}
        className="flex flex-1 items-center gap-3 px-3 py-2 text-left"
      >
        {/*
          Renders nothing at all when there is no media, rather than a box
          where a picture would go — so the row is the same shape before the
          media arrives and after it has gone.
        */}
        <ExerciseMedia
          media={media}
          mediaKey={option.mediaKey}
          name={option.name}
          className="w-12 shrink-0"
        />
        <span className="flex flex-col gap-0.5">
          <span className="text-ink text-sm">{option.name}</span>
          {detail === '' ? null : <span className="text-ink-muted text-xs">{detail}</span>}
        </span>
      </button>

      {onEdit ? (
        <button
          type="button"
          aria-label={`Make ${option.name} yours`}
          onMouseDown={(event) => {
            event.preventDefault();
            onEdit(option);
          }}
          className="text-primary px-3 py-2 text-xs underline-offset-2 hover:underline"
        >
          Make it yours
        </button>
      ) : null}
    </div>
  );
}
